import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Landmark, CheckCircle2, AlertTriangle, Calendar, FileText, Plus } from 'lucide-react';
import type { BankSnapshot, LedgerEntry } from '../../../../../shared/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost/fhpf-api';

export const BankReconciliation: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [snapshots, setSnapshots] = useState<BankSnapshot[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [statementBalance, setStatementBalance] = useState('');
  const [statementDate, setStatementDate] = useState(new Date().toISOString().substring(0, 10));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // 1. Load snapshots
    const qSnap = query(collection(db, 'bankSnapshots'), orderBy('statementDate', 'desc'));
    const unsubSnap = onSnapshot(
      qSnap,
      (snap) => {
        const list: BankSnapshot[] = [];
        snap.forEach((doc) => list.push(doc.data() as BankSnapshot));
        setSnapshots(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Snapshots load error:', err.message);
        setLoading(false);
      }
    );

    // 2. Load ledger for live balance calculation
    const qLedger = query(collection(db, 'ledger'));
    const unsubLedger = onSnapshot(qLedger, (snap) => {
      const list: LedgerEntry[] = [];
      snap.forEach((doc) => list.push(doc.data() as LedgerEntry));
      setLedgerEntries(list);
    });

    return () => {
      unsubSnap();
      unsubLedger();
    };
  }, []);

  // Compute ledger-derived balance
  let ledgerDerivedBalance = 0;
  for (const entry of ledgerEntries) {
    const amt = entry.amount || 0;
    if (['contribution', 'late_fee', 'legacy_import'].includes(entry.type)) {
      ledgerDerivedBalance += amt;
    } else if (['expense', 'land_payment'].includes(entry.type)) {
      ledgerDerivedBalance -= amt;
    } else if (entry.type === 'reversal') {
      ledgerDerivedBalance += amt;
    }
  }

  const numStatementBalance = parseInt(statementBalance, 10) || 0;
  const currentDiscrepancy = numStatementBalance - ledgerDerivedBalance;

  const handleRecordSnapshot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!statementBalance) {
      alert('Please enter statement balance.');
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/bank-snapshots`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          statementBalance: numStatementBalance,
          statementDate,
          notes: notes.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Failed to record bank snapshot.');
      }

      setMessage({ type: 'success', text: 'Bank statement snapshot recorded.' });
      setStatementBalance('');
      setNotes('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to record bank snapshot.' });
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Landmark className="w-5 h-5 text-blue-500" />
          <span>Bank Statement Reconciliation</span>
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Reconciles the physical bank account statement against the immutable cryptographic ledger balance.
        </p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-2.5 text-xs sm:text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
              : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Snapshot Entry Form */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm p-5 sm:p-6">
        <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-4">
          Record New Statement Snapshot
        </h3>

        <form onSubmit={handleRecordSnapshot} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Statement Date
              </label>
              <input
                type="date"
                required
                value={statementDate}
                onChange={(e) => setStatementDate(e.target.value)}
                className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Statement Closing Balance (BDT)
              </label>
              <input
                type="number"
                required
                value={statementBalance}
                onChange={(e) => setStatementBalance(e.target.value)}
                placeholder="e.g. 1450000"
                className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Reconciliation Notes
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Monthly bank interest credited, uncleared check #012..."
              className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
            />
          </div>

          {/* Live Discrepancy Preview */}
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-navy-950/40 border border-gray-200 dark:border-navy-800 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm">
            <div>
              <span className="block text-gray-500 dark:text-gray-400">Ledger Derived:</span>
              <span className="font-bold text-gray-900 dark:text-white">{fmt(ledgerDerivedBalance)}</span>
            </div>
            <div>
              <span className="block text-gray-500 dark:text-gray-400">Statement Balance:</span>
              <span className="font-bold text-gray-900 dark:text-white">{fmt(numStatementBalance)}</span>
            </div>
            <div>
              <span className="block text-gray-500 dark:text-gray-400">Discrepancy:</span>
              <span
                className={`font-bold ${
                  currentDiscrepancy === 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-500 dark:text-red-400'
                }`}
              >
                {fmt(currentDiscrepancy)} {currentDiscrepancy === 0 ? '(Balanced)' : '(Discrepancy)'}
              </span>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold flex items-center gap-2 shadow-sm shadow-primary-600/20 disabled:opacity-60"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>{t('admin.recordSnapshot')}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Historical Snapshots */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-navy-800 font-bold text-gray-900 dark:text-white text-sm">
          Reconciliation History
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : snapshots.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">No bank statement snapshots recorded.</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-navy-800">
            {snapshots.map((s) => (
              <div key={s.snapshotId} className="p-4 sm:p-5 flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-gray-900 dark:text-white">{s.statementDate}</span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        s.discrepancy === 0
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'
                      }`}
                    >
                      {s.discrepancy === 0 ? 'Balanced' : `Discrepancy: ${fmt(s.discrepancy)}`}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    <span>Ledger: {fmt(s.ledgerDerivedBalance)}</span>
                    <span className="mx-2">•</span>
                    <span>Statement: {fmt(s.statementBalance)}</span>
                    {s.notes && <span className="ml-2 font-medium italic">"{s.notes}"</span>}
                  </div>
                </div>

                <div className="text-xs text-gray-400 font-mono">
                  {s.snapshotId}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
